import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'

export interface CompanySettings {
  id?: string
  company_id?: string
  cor_primaria: string
  cor_secundaria: string
  cor_acento: string
  logo_url: string | null
  nome_empresa: string | null
  razao_social: string | null
  cnpj: string | null
  endereco_empresa: string | null
  telefone_empresa: string | null
  email_empresa: string | null
  rodape_relatorio: string | null
}

const DEFAULTS: CompanySettings = {
  cor_primaria: '#1a1a2e',
  cor_secundaria: '#0f3460',
  cor_acento: '#f59e0b',
  logo_url: null,
  nome_empresa: null,
  razao_social: null,
  cnpj: null,
  endereco_empresa: null,
  telefone_empresa: null,
  email_empresa: null,
  rodape_relatorio: null,
}

let cachedSettings: CompanySettings | null = null
let cachedCompanyId: string | null = null

export function useCompanySettings() {
  const { user } = useAuth()
  const companyId = user?.company_id
  const [settings, setSettings] = useState<CompanySettings>(cachedSettings && cachedCompanyId === companyId ? cachedSettings : DEFAULTS)
  const [loading, setLoading] = useState(!cachedSettings || cachedCompanyId !== companyId)

  const load = useCallback(async () => {
    if (!companyId) return
    if (cachedSettings && cachedCompanyId === companyId) {
      setSettings(cachedSettings)
      setLoading(false)
      return
    }
    setLoading(true)
    const { data } = await supabase
      .from('company_settings')
      .select('*')
      .eq('company_id', companyId)
      .maybeSingle()

    const s: CompanySettings = data ? {
      id: data.id,
      company_id: data.company_id,
      cor_primaria: data.cor_primaria || DEFAULTS.cor_primaria,
      cor_secundaria: data.cor_secundaria || DEFAULTS.cor_secundaria,
      cor_acento: data.cor_acento || DEFAULTS.cor_acento,
      logo_url: data.logo_url,
      nome_empresa: data.nome_empresa,
      razao_social: data.razao_social,
      cnpj: data.cnpj,
      endereco_empresa: data.endereco_empresa,
      telefone_empresa: data.telefone_empresa,
      email_empresa: data.email_empresa,
      rodape_relatorio: data.rodape_relatorio,
    } : DEFAULTS

    cachedSettings = s
    cachedCompanyId = companyId
    setSettings(s)
    setLoading(false)
  }, [companyId])

  useEffect(() => { load() }, [load])

  const refresh = useCallback(() => {
    cachedSettings = null
    cachedCompanyId = null
    return load()
  }, [load])

  return { settings, loading, refresh }
}
